import { useFormContext } from 'react-hook-form';
import { ResultPeriod } from '../ui';
import { CriteriaTime } from '@src/api';
import { FC, useContext, useEffect, useState } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import { DiagnosesModal } from './components/DiagnosesModal';
import { DisableContext } from '@pages/Selection/components/DisableContext';
import { DisabledRuleItem } from '@components/DisabledRuleItem';
import { Variant } from '@mui/material/styles/createTypography';
import { buildDataTestIdAttribute, buildDataTestIdFromNestedName } from '@src/utils';

const diagnosisCodeRegex = /\([A-Z]\d{1,2}[A-Z]?(?:-[A-Z]\d{1,2}[A-Z]?)?\)$/;

type DiagnosesRuleProps = {
  prefix: string;
  children: JSX.Element;
};

const SelectedDiagnoses = ({
  diagnoses,
  diagnosesGroups,
  withBorder = true,
  sx = {},
  gap = '0px',
  variant = 'label14Medium',
}) => {
  return (
    <Stack width="100%">
      {diagnoses?.length > 0 && (
        <Stack width="100%" gap={gap}>
          {diagnoses?.map(({ name, description }, index, arr) => {
            const hasCodeInDescription = diagnosisCodeRegex.test(description as string);
            const displayDescription = hasCodeInDescription ? description : `${description} (${name})`;

            return (
              <Box
                key={name}
                padding="9px 0"
                borderBottom={
                  !withBorder || (index === arr.length - 1 && diagnosesGroups?.length === 0)
                    ? 'none'
                    : '1px solid #CFEBF0'
                }
                sx={sx}
              >
                <Typography color="grey.700" variant={variant as Variant}>
                  {`${displayDescription}`}
                </Typography>
              </Box>
            );
          })}
        </Stack>
      )}
      {diagnosesGroups?.length > 0 && (
        <Stack width="100%" gap={gap}>
          {diagnosesGroups?.map(({ id, name }, index, arr) => (
            <Box
              key={`${name}${id}`}
              padding="9px 0"
              borderBottom={index === arr.length - 1 ? 'none' : '1px solid #CFEBF0'}
              sx={sx}
            >
              <Typography color="grey.700" variant={variant as Variant}>
                {`${name} (Diagnosis group)`}
              </Typography>
            </Box>
          ))}
        </Stack>
      )}
    </Stack>
  );
};

export const DiagnosesRule: FC<DiagnosesRuleProps> = ({ prefix, children }) => {
  const { getValues, setValue, watch } = useFormContext();

  const disabled = useContext(DisableContext);

  const [openModal, setOpenModal] = useState(false);

  const scaleTypeName = `${prefix}.scaleType`;

  const [scaleType, scaleValue] = getValues([scaleTypeName, `${prefix}.scaleValue`]);

  const handleClickScaleType = (value: CriteriaTime) => {
    setValue(scaleTypeName, value);
  };

  const [diagnoses, diagnosesGroups] = watch([`${prefix}.diagnoses`, `${prefix}.diagnosesGroups`]);

  useEffect(() => {
    if (diagnoses || diagnosesGroups) {
      setValue(`${prefix}.diagnosisIds`, diagnoses.length > 0 ? diagnoses.map(({ id }) => id) : null, {
        shouldValidate: true,
      });
      setValue(`${prefix}.diagnosisGroupIds`, diagnosesGroups.length > 0 ? diagnosesGroups.map(({ id }) => id) : null);
    }
  }, [diagnoses, diagnosesGroups, prefix, setValue]);

  const onClickAdd = () => {
    setOpenModal(true);
  };

  const onAdd = (
    diagnosesInRule: { name: string; id: string; description: string }[],
    diagnosesGroupsInRule: { name: string; id: number }[],
  ) => {
    setValue(`${prefix}.diagnosisIds`, diagnosesInRule.length > 0 ? diagnosesInRule.map(({ id }) => id) : null, {
      shouldValidate: true,
    });
    setValue(
      `${prefix}.diagnosisGroupIds`,
      diagnosesGroupsInRule.length > 0 ? diagnosesGroupsInRule.map(({ id }) => id) : null,
    );
    setValue(`${prefix}.diagnoses`, diagnosesInRule);
    setValue(`${prefix}.diagnosesGroups`, diagnosesGroupsInRule);

    setOpenModal(false);
  };

  const dataTestId = buildDataTestIdFromNestedName(`${prefix}.addDiagnosis`, 'button');

  return disabled ? (
    <DisabledRuleItem
      name="Conditions and diagnoses"
      value={`${scaleValue} ${CriteriaTime[scaleType].toLowerCase()}${scaleValue > 1 ? 's' : ''}`}
      valueName="In the last"
      description={
        <SelectedDiagnoses
          diagnoses={diagnoses}
          diagnosesGroups={diagnosesGroups}
          withBorder={false}
          sx={{ padding: '0' }}
          gap="12px"
          variant="label12Medium"
        />
      }
    />
  ) : (
    <>
      <ResultPeriod
        scaleType={scaleType}
        handleClickScaleType={handleClickScaleType}
        prefix={prefix}
        withBiomarker={false}
        disabled={disabled}
        resultType="diagnoses"
      >
        <>
          <Button
            color="secondary"
            variant="contained"
            onClick={onClickAdd}
            disabled={disabled}
            {...buildDataTestIdAttribute(dataTestId)}
          >
            {diagnoses?.length > 0 || diagnosesGroups?.length > 0 ? 'Edit' : 'Add'}
          </Button>
          {children}
          <SelectedDiagnoses diagnoses={diagnoses} diagnosesGroups={diagnosesGroups} />
        </>
      </ResultPeriod>
      <DiagnosesModal open={openModal} setOpen={setOpenModal} onAdd={onAdd} prefix={prefix} />
    </>
  );
};
